import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isEmpty } from 'lodash';

import { AccessLevelIcon } from '@dbt-labs/dbt-dag';
import { LinkMapping } from '@dbt-labs/metadata-shared';
import {
  CellContext,
  ColumnDef,
  DropdownOption,
  Icon,
  Link,
  RyeconEyeClosed,
  RyeconInfoOutline,
  SortingState,
  Tooltip,
} from '@dbt-labs/sourdough';

import {
  AccessLevel,
  HealthIssueType,
  PopularModelSortField,
  SortDirection,
  SourceHealthIssueType,
} from '@/__generated__/graphql';
import { PaginatedTable, PaginationType } from '@/components/PaginatedTable';
import { Spinner } from '@/components/Spinner';
import { CustomCell } from '@/components/Table/CustomCell';
import { ModelTrustSignalCell } from '@/components/Table/ModelTrustSignalCell';
import { OneLineHeader } from '@/components/Table/TableHeader';
import { RightAlignedTruncatedCell, TruncatedCell } from '@/components/TruncatedCell';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { ResourceByEnvironmentParams } from '@/hooks/discovery/resource';
import { useLinkToExplorer } from '@/hooks/links';
import { useAllProjects } from '@/hooks/useAllProjects';
import { useGetLinkMapping } from '@/hooks/useGetLinkMapping';
import { useTargetEnvironmentType } from '@/hooks/useTargetEnvironmentType';
import { safeParseUniqueId } from '@/typings/parseUniqueId';
import { toTitleCase } from '@/util/string';

type DependentProject = {
  projectId?: number;
  dependentModelsCount?: number;
  defaultEnvironmentId?: number;
  dbtCoreProject?: string;
};

export interface PopularModel {
  access: string | null;
  projectId: number;
  environmentId: number;
  uniqueId: string;
  // runGeneratedAt: string;
  dependentProjects?: DependentProject[];
  lastRunStatus?: string | null;
  healthIssues: HealthIssueType[] | null;
  hasAccess: boolean;
  queryUsage30d: number;
  queryHistorySuccess: boolean;
  modelLayer: string | null;
  group: string | null;
}

export interface PopularModelsTableProps {
  paginationType: PaginationType;
  pageOptions: DropdownOption[];
  pageSize: number;
  isLoading: boolean;
  isInitialLoad?: boolean;
  isLoadingMore?: boolean;
  hasMoreResults: boolean;
  popularModels: PopularModel[];
  fetchMore: () => void;
  setSelectedResourceParams: (params: ResourceByEnvironmentParams) => void;
  setIsColumnsPanelVisible: (isVisible: boolean) => void;
  totalCount: number;
}

const _sortableColumnNames = ['name', 'executeCompletedAt', 'queryUsage30d'] as const;
type SortableColumnName = (typeof _sortableColumnNames)[number];
const SORTABLE_COLS = {
  name: PopularModelSortField.Name,
  executeCompletedAt: PopularModelSortField.ExecuteCompletedAt,
  queryUsage30d: PopularModelSortField.QueryUsage30d,
};

export const PopularModelsTable: React.FC<PopularModelsTableProps> = ({
  paginationType,
  pageOptions,
  pageSize,
  isLoading,
  isLoadingMore = false,
  hasMoreResults,
  popularModels,
  fetchMore,
  setSelectedResourceParams,
  setIsColumnsPanelVisible,
  totalCount,
}) => {
  const { accountId } = useDbtCloudAccount();
  const { data: allProjects, isLoading: isLoadingProjects } = useAllProjects(accountId);
  const [, setSearchParams] = useSearchParams();

  const onChangeSort = useCallback(
    (sortRule: SortingState) => {
      if (!sortRule?.length) {
        return;
      }
      const sortCol: string = sortRule[0].id;
      const sortDir = sortRule[0].desc ? SortDirection.Desc : SortDirection.Asc;

      setSearchParams((p) => {
        p.set('popularModelsSortField', SORTABLE_COLS[sortCol as SortableColumnName]);
        p.set('popularModelsSortDir', sortDir);
        return p;
      });
    },
    [setSearchParams],
  );
  // Create a map from projectId to project name
  const projectIdToNameMap = useMemo(() => {
    if (!allProjects) return {};
    return allProjects.reduce(
      (acc, project) => {
        if (project.projectId) {
          acc[project.projectId] =
            project.dbtCloudProject || `Project ${project.projectId}`;
        }
        return acc;
      },
      {} as Record<number, string>,
    );
  }, [allProjects]);

  const getTargetEnvironmentType = useTargetEnvironmentType();
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');
  const getProjectLink = useLinkToExplorer('project');
  const getLinkMapping = useGetLinkMapping();

  const columns = useMemo((): ColumnDef<PopularModel, any>[] => {
    return [
      {
        header: 'Name',
        accessorFn: (row: PopularModel) => {
          const uniqueId = safeParseUniqueId(row.uniqueId).ok;
          return (
            <>
              <span>{uniqueId ? uniqueId.remainder : row.uniqueId}</span>
              {!row.hasAccess && (
                <Tooltip
                  className="inline-block text-base font-normal text-fgMain"
                  content="You do not have access to the project that owns this"
                >
                  <Icon
                    testId="noAccessIcon"
                    size="xs"
                    className="ml-1 mt-1 align-text-top text-fgMain"
                    ryecon={RyeconEyeClosed}
                  />
                </Tooltip>
              )}
            </>
          );
        },
        id: 'name',
        cell: ({
          getValue,
          row: {
            original: { projectId, environmentId, uniqueId, hasAccess },
          },
        }: CellContext<PopularModel, any>) =>
          hasAccess ? (
            <Link
              isInternal
              to={getDetailsPageLink({
                accountId: accountId ?? 0,
                projectId: projectId,
                environmentType: getTargetEnvironmentType(environmentId),
                dbtUniqueId: uniqueId,
              })}
            >
              {getValue()}
            </Link>
          ) : (
            <span className="text-fgAlt">{getValue()}</span>
          ),
        enableSorting: true,
        sortDescFirst: true,
      },
      {
        header: 'Description',
        accessorKey: 'description',
        id: 'description',
        cell: TruncatedCell,
        enableSorting: false,
      },
      {
        header: 'Modeling layer',
        accessorKey: 'modelLayer',
        id: 'modelLayer',
        accessorFn: (data) => toTitleCase(data.modelLayer ?? ''),
        cell: TruncatedCell,
        minSize: 120,
        maxSize: 120,
        enableSorting: false,
      },
      {
        header: () => (
          <OneLineHeader
            title={
              <span className="flex space-x-1 align-middle">
                <span>Query count</span>
                <Tooltip
                  content={
                    <div className="whitespace-normal text-wrap">
                      The number of consumed queries against this resource for the last
                      30 days. This feature must be enabled per environment.
                    </div>
                  }
                  placement="top-end"
                >
                  <RyeconInfoOutline />
                </Tooltip>
              </span>
            }
            align="right"
          />
        ),
        accessorKey: 'queryUsage30d',
        id: 'queryUsage30d',
        cell: (cellProps: CellContext<PopularModel, any>) => {
          const value = cellProps.getValue();
          const { queryHistorySuccess } = cellProps.row.original;

          if (!queryHistorySuccess) {
            return (
              <div className="w-full text-right">
                <Tooltip content="Query history data is not available for this environment">
                  <div className="inline-block">
                    <RightAlignedTruncatedCell
                      {...cellProps}
                      getValue={() => 'N/A' as any}
                    />
                  </div>
                </Tooltip>
              </div>
            );
          }

          const formattedValue =
            typeof value === 'number' ? value.toLocaleString() : value;
          return (
            <RightAlignedTruncatedCell {...cellProps} getValue={() => formattedValue} />
          );
        },
        enableSorting: true,
        minSize: 120,
        maxSize: 120,
      },
      {
        header: () => <OneLineHeader title="Model access" />,
        accessorKey: 'access',
        accessorFn: (data) => ({
          access: data.access,
        }),
        size: 30,
        minSize: 120,
        enableSorting: false,
        cell: ({
          row: {
            original: { access },
          },
        }) => {
          const accessLevel = access as AccessLevel;
          return (
            <CustomCell tooltip={accessLevel}>
              <span className="flex items-center gap-2">
                {access && (
                  <AccessLevelIcon
                    size="sm"
                    access={accessLevel}
                    tooltipConfig={{ placement: 'bottom' }}
                    iconClassName="h-4 w-4 text-fgMain"
                  />
                )}
                {toTitleCase(accessLevel)}
              </span>
            </CustomCell>
          );
        },
      },
      {
        header: 'Health',
        accessorKey: 'healthIssues',
        cell: (
          cellProps: CellContext<
            PopularModel,
            (HealthIssueType | SourceHealthIssueType)[]
          >,
        ) => {
          const values = cellProps.getValue();
          const { environmentId, projectId, uniqueId } = cellProps.row.original;
          const environmentType = getTargetEnvironmentType(environmentId);
          /**
           * transform from an array of health issues to a link mapping
           * e.g [HealthIssueType.CautionUpstreamSources] to { CautionUpstreamSources: { to: '...', search: '...' } }
           */
          const linkMapping: LinkMapping = getLinkMapping({
            healthIssues: values,
            projectId,
            environmentType,
            uniqueId,
          });
          return (
            <ModelTrustSignalCell
              {...cellProps}
              additionalMetadata={!isEmpty(linkMapping) ? { linkMapping } : undefined}
            />
          );
        },
        minSize: 100,
        maxSize: 200,
        enableSorting: false,
      },
      {
        header: 'Project',
        accessorKey: 'projectId',
        id: 'projectId',
        cell: ({
          getValue,
          row: {
            original: { projectId, environmentId },
          },
        }: CellContext<PopularModel, any>) => {
          if (isLoadingProjects) {
            return <Spinner />;
          }

          return (
            <Link
              isInternal
              to={getProjectLink({
                accountId: accountId ?? 0,
                projectId: projectId,
                environmentType: getTargetEnvironmentType(environmentId),
              })}
            >
              {projectIdToNameMap[projectId] || getValue()}
            </Link>
          );
        },
        enableSorting: false,
      },
      {
        header: 'Owner',
        accessorKey: 'group',
        cell: TruncatedCell,
        minSize: 120,
        enableSorting: false,
      },
      {
        header: 'Columns',
        accessorKey: 'columnCount',
        id: 'columnCount',
        cell: ({ getValue, row: { original } }: CellContext<PopularModel, any>) => {
          const { environmentId, uniqueId, hasAccess } = original;
          const shouldShowPanelLink = hasAccess && getValue() !== 0;
          return (
            <Tooltip
              displayOnlyWhenTruncated
              content={getValue()}
              className="w-full"
              childrenAreInteractive={true}
            >
              {(ref) => (
                <div
                  ref={ref}
                  className={`z-10 truncate ${shouldShowPanelLink ? 'text-fgBrand underline' : ''}`}
                  onClick={() => {
                    if (!shouldShowPanelLink) return;
                    setSelectedResourceParams({
                      environmentId: environmentId,
                      uniqueId: uniqueId,
                    });
                    setIsColumnsPanelVisible(true);
                  }}
                >
                  {getValue()}
                </div>
              )}
            </Tooltip>
          );
        },
        enableSorting: false,
        size: 60,
        maxSize: 100,
      },
      {
        header: 'Last executed',
        accessorKey: 'executeCompletedAt',
        id: 'executeCompletedAt',
        cell: TruncatedCell,
        minSize: 120,
        enableSorting: true,
        sortDescFirst: true,
      },
    ];
  }, [
    accountId,
    getDetailsPageLink,
    getProjectLink,
    getTargetEnvironmentType,
    getLinkMapping,
    setSelectedResourceParams,
    setIsColumnsPanelVisible,
    projectIdToNameMap,
    isLoadingProjects,
  ]);

  const pageInfo = {
    totalCount: totalCount,
    hasNextPage: hasMoreResults,
    endCursor: null,
  };

  const pageDisplayLabel =
    popularModels?.length == null || pageInfo?.totalCount == null
      ? undefined
      : `Loaded ${popularModels.length} of ${pageInfo.totalCount}`;

  if (!accountId) {
    return null;
  }

  return (
    <PaginatedTable
      paginationType={paginationType}
      columns={columns}
      data={popularModels}
      isSortable={true}
      loadingRowCount={pageSize}
      isLoading={isLoading}
      maxResultCount={totalCount}
      pageSize={pageSize}
      emptyStateProps={{
        header: `No results`,
        helpText: `No results were found`,
      }}
      pagination={{
        selectedPageCount: pageOptions.find(({ value }) => value === pageSize),
        pageOptions,
        shouldShowLoadMore: !isLoadingMore && pageInfo?.hasNextPage,
        hasMorePages: pageInfo?.hasNextPage,
        onLoadMore: fetchMore,
        isPageLoading: isLoadingMore,
        pageDisplayLabel,
      }}
      onChangeSort={onChangeSort}
      initialSortColumn="queryUsage30d"
      initialSortDesc={true}
    />
  );
};
